/**
 * Encrypted user profile cache keyed by Entra Object ID.
 * Reduces UserService GET /users/v1/entra calls by caching profile in secure storage.
 * Re-fetch only on cache miss, schema invalidation, profile update (write-through), or logout (invalidate).
 */
import { getSecureJSON, setSecureJSON, removeSecureItem } from '@hsus-dev/session-policy';
import { userProfileSchema, type UserProfile } from '../validation/userProfileSchemas';
import { UserProfileApi } from '../api/userProfile';

const CACHE_VERSION = 'v1';
const FAIL_TTL_MS = 60_000;

const PROFILE_KEY_PREFIX = `user_profile_cache_${CACHE_VERSION}_`;
const FAIL_KEY_PREFIX = `user_profile_cache_fail_${CACHE_VERSION}_`;

function profileKey(entraObjectId: string): string {
  return `${PROFILE_KEY_PREFIX}${entraObjectId}`;
}

function failKey(entraObjectId: string): string {
  return `${FAIL_KEY_PREFIX}${entraObjectId}`;
}

/** UUID format check only (any version). Do not claim v4 unless enforced. */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Thrown when fail-marker is valid and not bypassed. Call sites can show a stable message
 * and use retryAfterMs for telemetry or UI.
 */
export class ProfileFetchSuppressedError extends Error {
  readonly name = 'ProfileFetchSuppressedError';
  constructor(
    message: string,
    public readonly entraObjectId: string,
    public readonly reason?: string,
    public readonly retryAfterMs: number = 0
  ) {
    super(message);
    Object.setPrototypeOf(this, ProfileFetchSuppressedError.prototype);
  }
}

/** UUID format check (single canonical place). Use for fast-fail and logout invalidation. */
export function isEntraObjectId(value: string): boolean {
  return typeof value === 'string' && UUID_REGEX.test(value.trim());
}

interface FailMarker {
  failedAt: number;
  reason?: string;
}

function getBoundedReason(error: unknown): string {
  if (error && typeof error === 'object') {
    const err = error as { response?: { status?: number }; code?: string; message?: string };
    if (typeof err.response?.status === 'number') {
      if (err.response.status >= 500) return 'http_503';
      if (err.response.status === 408) return 'timeout';
      return `http_${err.response.status}`;
    }
    if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') return 'timeout';
    if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED') return 'network_error';
  }
  return 'unknown_error';
}

const inflight = new Map<string, Promise<UserProfile | null>>();

const DEV_METRICS = typeof import.meta !== 'undefined' && import.meta.env?.DEV;

function logMetric(event: string, data?: Record<string, unknown>): void {
  if (DEV_METRICS && typeof console !== 'undefined' && console.debug) {
    console.debug('[userProfileCache]', event, data ?? '');
  }
}

/**
 * Get profile from cache or fetch from API. Throws on non-UUID input.
 * Use tryGetCachedOrFetch where null is acceptable.
 */
export async function getCachedOrFetch(
  entraObjectId: string,
  options?: { headers?: Record<string, string>; forceRefresh?: boolean; bypassFailMarker?: boolean }
): Promise<UserProfile | null> {
  if (!isEntraObjectId(entraObjectId)) {
    throw new Error(`userProfileCache: entraObjectId must be a UUID format string, got: ${typeof entraObjectId}`);
  }

  const { forceRefresh = false, bypassFailMarker = false, headers } = options ?? {};

  // Inflight dedupe (always, including when forceRefresh)
  const existing = inflight.get(entraObjectId);
  if (existing) {
    logMetric('inflight_dedup', { entraObjectId });
    return existing;
  }

  const run = async (): Promise<UserProfile | null> => {
    try {
      // Fail-marker check (even when forceRefresh, unless bypassed)
      if (!bypassFailMarker) {
        const failRaw = await getSecureJSON<FailMarker>(failKey(entraObjectId));
        if (failRaw && typeof failRaw.failedAt === 'number') {
          const elapsed = Date.now() - failRaw.failedAt;
          if (elapsed < FAIL_TTL_MS) {
            const retryAfterMs = FAIL_TTL_MS - elapsed;
            logMetric('fail_marker_suppress', { entraObjectId, retryAfterMs });
            throw new ProfileFetchSuppressedError(
              'Profile fetch suppressed (recent failure). Retry after backoff.',
              entraObjectId,
              failRaw.reason,
              retryAfterMs
            );
          }
        }
      }

      // Cache read (skip when forceRefresh)
      if (!forceRefresh) {
        const cached = await getSecureJSON<unknown>(profileKey(entraObjectId));
        if (cached != null) {
          const parsed = userProfileSchema.safeParse(cached);
          if (parsed.success && parsed.data) {
            logMetric('cache_hit', { entraObjectId });
            return parsed.data;
          }
          await removeSecureItem(profileKey(entraObjectId));
        }
      }

      logMetric('cache_miss', { entraObjectId });
      const profile = await UserProfileApi.getByEntraObjectId(entraObjectId, { headers });
      if (profile) {
        await setSecureJSON(profileKey(entraObjectId), profile);
        await removeSecureItem(failKey(entraObjectId));
      }
      return profile;
    } catch (err) {
      if (err instanceof ProfileFetchSuppressedError) throw err;
      const reason = getBoundedReason(err);
      await setSecureJSON(failKey(entraObjectId), { failedAt: Date.now(), reason });
      throw err;
    } finally {
      inflight.delete(entraObjectId);
    }
  };

  const promise = run();
  inflight.set(entraObjectId, promise);
  return promise;
}

/**
 * Optional helper when null is acceptable (e.g. optional profile). Catches throw from non-UUID or suppress.
 */
export async function tryGetCachedOrFetch(
  entraObjectId: string,
  options?: { headers?: Record<string, string>; forceRefresh?: boolean; bypassFailMarker?: boolean }
): Promise<UserProfile | null> {
  if (!entraObjectId || !isEntraObjectId(entraObjectId)) return null;
  try {
    return await getCachedOrFetch(entraObjectId, options);
  } catch {
    return null;
  }
}

/**
 * Write profile to cache. Validates with userProfileSchema first; deletes fail-marker for this user.
 */
export async function setCachedProfile(entraObjectId: string, profile: UserProfile): Promise<void> {
  if (!isEntraObjectId(entraObjectId)) {
    throw new Error(`userProfileCache: entraObjectId must be a UUID format string, got: ${typeof entraObjectId}`);
  }
  const parsed = userProfileSchema.safeParse(profile);
  if (!parsed.success) {
    throw new Error('userProfileCache: profile failed schema validation');
  }
  await setSecureJSON(profileKey(entraObjectId), parsed.data);
  await removeSecureItem(failKey(entraObjectId));
}

/**
 * Remove profile and fail-marker for this user. Sync to match removeSecureItem.
 */
export function invalidateProfileCache(entraObjectId: string): void {
  if (!entraObjectId) return;
  removeSecureItem(profileKey(entraObjectId));
  removeSecureItem(failKey(entraObjectId));
  inflight.delete(entraObjectId);
}
